import { useMemo } from 'react'
import { FaSync } from 'react-icons/fa'
import { useServiceRequests } from '@/hooks/useRequests'
import { useQuoteRequests } from '@/hooks/useQuoteRequests'
import Button from '@/components/common/Button/Button'
import RequestCard from '@/components/cards/RequestCard/RequestCard'
import './Requests.css'

const Requests = () => {
    const { data: serviceRequests, isLoading: isLoadingServiceRequests, isError: isErrorServiceRequests, refetch: refetchServiceRequests } = useServiceRequests()
    const { data: quoteRequests, isLoading: isLoadingQuoteRequests, isError: isErrorQuoteRequests, refetch: refetchQuoteRequests } = useQuoteRequests()
    
    const isLoading = isLoadingServiceRequests || isLoadingQuoteRequests
    const isError = isErrorServiceRequests || isErrorQuoteRequests
    
    // Combine and sort by creation date (newest first)
    const list = useMemo(() => {
        const serviceList = (serviceRequests || []).map((r: any) => ({
            ...r,
            type: 'service_request' as const,
            displayId: r.id,
            displayDate: r.created,
        }))
        
        const quoteList = (quoteRequests || []).map((q: any) => ({
            ...q,
            type: 'quote_request' as const,
            displayId: q.id,
            displayDate: q.created,
            // Map quote request fields to match RequestCard interface
            service_name: q.service_name || q.service?.name || 'Servicio',
            status: q.status,
            description: q.description,
            estimated_price: q.quoted_total || q.offering_details?.base_quote_amount,
            location_details: q.district_details ? { address: q.district_details.name } : undefined,
        }))
        
        const combined = [...serviceList, ...quoteList]
        return combined.sort((a, b) => {
            const dateA = new Date(a.displayDate).getTime()
            const dateB = new Date(b.displayDate).getTime()
            return dateB - dateA
        })
    }, [serviceRequests, quoteRequests])
    
    const refetch = () => {
        refetchServiceRequests()
        refetchQuoteRequests()
    }

    return (
        <div className="requests-page">
            <div className="requests-header">
                <div className="requests-header-content">
                    <h1 className="requests-title">Mis Solicitudes</h1>
                    <p className="requests-subtitle">{list.length} solicitud{list.length !== 1 ? 'es' : ''}</p>
                </div>
                <Button
                    variant="primary"
                    size="md"
                    onClick={() => refetch()}
                >
                    <FaSync /> Actualizar
                </Button>
            </div>
            <div className="requests-container">
                
                {isLoading ? (
                    <div className="requests-empty-state">
                        <p>Cargando solicitudes...</p>
                    </div>
                ) : isError ? (
                    <div className="requests-empty-state">
                        <p>Error al cargar solicitudes</p>
                    </div>
                ) : list.length === 0 ? (
                    <div className="requests-empty-state">
                        <p>No tienes solicitudes aún</p>
                        <p className="requests-empty-hint">Tus solicitudes de servicios aparecerán aquí</p>
                    </div>
                ) : (
                    <div className="requests-grid">
                        {list.map((r: any) => (
                            <RequestCard 
                                key={`${r.type}-${r.id}`} 
                                request={{
                                    id: r.displayId,
                                    type: r.type,
                                    service_name: r.service_name || r.service?.name || 'Servicio',
                                    description: r.description,
                                    status: r.status,
                                    estimated_price: r.type === 'service_request' 
                                        ? (r.final_price || r.proposed_price || r.estimated_price)
                                        : (r.quoted_total || r.estimated_price),
                                    scheduled_date: r.scheduled_date,
                                    scheduled_hour: r.scheduled_hour,
                                    estimated_duration_hours: r.estimated_duration_hours,
                                    location_details: r.location_details || (r.location_address ? { address: r.location_address } : undefined),
                                    created_at: r.displayDate
                                }}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

export default Requests


